// 10.19 The happiness check before the review ask (migration 20261201090000; engine/happiness.py). The public answer
// page (/how, screens/HowDidWeDo.tsx) talks to the intake function's /happiness routes; the monthly figures on
// Performance come from crm_happiness_by_month. No review gating: nothing here reads or changes a review request.
import { functionUrl } from "./env";
import { httpFetch } from "./http";

export const ANSWERS = ["happy", "okay", "unhappy"] as const;
export type Answer = (typeof ANSWERS)[number];
export const COMMENT_MAX = 2000;

export const ANSWER_LABEL: Record<Answer, string> = { happy: "Happy", okay: "Okay", unhappy: "Unhappy" };
/** The words on the page's three buttons. */
export const ANSWER_BUTTON: Record<Answer, string> = {
  happy: "Happy: it went well",
  okay: "Okay: it was fine",
  unhappy: "Unhappy: something was wrong",
};

/** crm_happiness_by_month: one month's figures (UTC month, "2026-12"). Shares are of the answers that month. */
export interface HappinessMonth {
  month: string;
  sent: number;
  happy: number;
  okay: number;
  unhappy: number;
  answered: number;
  happy_share: number | null;
  okay_share: number | null;
  unhappy_share: number | null;
  no_answer: number;
  review_requests: number;
  reviews: number;
  average_rating: number | null;
}

const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : Number(v) || 0);
const share = (v: unknown): number | null => (v === null || v === undefined || v === "" ? null : num(v));

/** The database's rows, newest month first, with every figure a number. */
export function readHappinessMonths(raw: unknown): HappinessMonth[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r): r is Record<string, unknown> => !!r && typeof r === "object" && typeof (r as { month?: unknown }).month === "string")
    .map((r) => ({
      month: String(r.month),
      sent: num(r.sent),
      happy: num(r.happy),
      okay: num(r.okay),
      unhappy: num(r.unhappy),
      answered: num(r.answered),
      happy_share: share(r.happy_share),
      okay_share: share(r.okay_share),
      unhappy_share: share(r.unhappy_share),
      no_answer: num(r.no_answer),
      review_requests: num(r.review_requests),
      reviews: num(r.reviews),
      average_rating: share(r.average_rating),
    }))
    .sort((a, b) => (a.month < b.month ? 1 : a.month > b.month ? -1 : 0));
}

/** "Dec 2026" for "2026-12". */
export function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
}

/** "60% happy · 30% okay · 10% unhappy", or "No answers yet". */
export function sharesText(m: Pick<HappinessMonth, "answered" | "happy_share" | "okay_share" | "unhappy_share">): string {
  if (!m.answered) return "No answers yet";
  const pc = (x: number | null) => `${Math.round((x ?? 0) * 100)}%`;
  return `${pc(m.happy_share)} happy · ${pc(m.okay_share)} okay · ${pc(m.unhappy_share)} unhappy`;
}

/** True when there is nothing at all to show yet (no checks, requests or reviews in any month). */
export function allQuiet(months: HappinessMonth[]): boolean {
  return months.every((m) => !m.sent && !m.answered && !m.review_requests && !m.reviews);
}

// ---------------------------------------------------------------------------
// The public answer page
// ---------------------------------------------------------------------------
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The business and the token from the link (?t=<tenant>&k=<token>), or null when the link is broken. */
export function linkParams(search: string): { t: string; k: string } | null {
  const q = new URLSearchParams(search);
  const t = q.get("t") ?? "";
  const k = q.get("k") ?? "";
  return UUID.test(t) && UUID.test(k) ? { t, k } : null;
}

export interface PageState {
  found: boolean;
  business?: string;
  answered?: boolean;
  answer?: Answer;
  expired?: boolean;
}

const endpoint = (base?: string) => `${functionUrl("intake", base)}/happiness`;

/** What the page shows for a link (records nothing). */
export async function loadPage(p: { t: string; k: string }, base?: string): Promise<PageState> {
  const res = await httpFetch(`${endpoint(base)}?t=${encodeURIComponent(p.t)}&k=${encodeURIComponent(p.k)}`);
  const body = (await res.json().catch(() => ({}))) as PageState;
  return { found: Boolean(body.found), business: body.business, answered: body.answered, answer: body.answer, expired: body.expired };
}

/** The customer's answer and optional comment. Throws with the page's words when it is refused. */
export async function sendAnswer(
  p: { t: string; k: string },
  answer: Answer,
  comment: string,
  base?: string,
): Promise<{ recorded: boolean; already: boolean; answer: Answer }> {
  const res = await httpFetch(endpoint(base), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ t: p.t, k: p.k, answer, comment: comment.trim() ? comment.trim().slice(0, COMMENT_MAX) : null }),
  });
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; recorded?: boolean; already?: boolean; answer?: Answer };
  if (!res.ok || !body.ok) throw new Error(body.error ?? "Something went wrong. Please try again.");
  return { recorded: Boolean(body.recorded), already: Boolean(body.already), answer: body.answer ?? answer };
}

/** The page lives at /how, outside sign-in. */
export const PUBLIC_PATH = "/how";
export function isPublicPath(pathname: string): boolean {
  return pathname === PUBLIC_PATH || pathname === `${PUBLIC_PATH}/`;
}
